import {
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeIcon,
  TeTag,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import CompanyIcon from '../Company/CompanyIcon';
import { formatAddress } from '../../utils/formatAddress';
import { formatOrganizationNumber } from '../../utils/organizationNumberFormatter';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

interface Props {
  suggestion: MigrationCandidateBusinessPartnerBody;
  /**
   * Which of the card's two variants to render. Derived by the page from
   * `needsConnect` and passed down rather than recomputed here, so the label
   * and the destination `onReview` leads to can never disagree — the card stays
   * presentational and imports no domain predicate.
   */
  mode: 'review' | 'connect';
  onReview: (suggestion: MigrationCandidateBusinessPartnerBody) => void;
  onRemove: (suggestion: MigrationCandidateBusinessPartnerBody) => void;
}

/**
 * Presentational card for a single dealer-scope Customer Suggestion.
 *
 * A sibling of `MigrationCandidateParentRow`, not a variant of it: the two
 * designs differ in border, tag placement and address treatment and are
 * diverging rather than converging, so they intentionally share no abstraction.
 *
 * Two variants — `review` and `connect` — exist because a Customer Suggestion's
 * next step is either to choose among its Potential Matches or, once it's
 * already linked to a Business Partner, to Connect (see **Exact Match** and
 * **Connect** in `GLOSSARY.md` — a row can carry both stale Potential Matches
 * and a link; only which button is offered is exclusive). The page derives
 * `mode` from `needsConnect` and passes it here, so the button's label and
 * where `onReview` leads are provably the same decision rather than two
 * decisions that happen to agree. Owns no data fetching, cache or modal
 * state; the page wires behaviour through `onReview` / `onRemove`.
 */
function CustomerSuggestionCard({ suggestion, mode, onReview, onRemove }: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'customerSuggestions' });
  const address = formatAddress(suggestion.address);
  const contextId = `customer-suggestion-${suggestion.id}`;
  const organizationNumber =
    mode === 'connect' && suggestion.businessPartner?.extId
      ? formatOrganizationNumber(suggestion.businessPartner.extId)
      : '';

  return (
    <TeContainer
      alignItems="center"
      justifyContent="space-between"
      gap="xl"
      fill
      padding={['s', 'm']}
      backgroundColor="background.secondary"
      borderColor="border.secondary"
      borderRadius="l"
    >
      <TeContainerSlim gap="m" alignItems="center" grow>
        <CompanyIcon size="l" />
        <TeContainerSlim column gap="xs" alignItems="flex-start" grow>
          <TeContainerSlim column gap="xs3" alignItems="flex-start">
            <TeContainerSlim gap="xs" alignItems="center">
              <TeText size={16} weight={500} truncate>
                {suggestion.name}
              </TeText>
              {organizationNumber && (
                <TeTag
                  size="s"
                  text={organizationNumber}
                  backgroundColor="background.tertiary"
                  textColor="text.primary"
                  borderColor="border.secondary"
                />
              )}
            </TeContainerSlim>
            {suggestion.sourceSystem && (
              <TeTag
                size="s"
                text={suggestion.sourceSystem}
                backgroundColor="background.tertiary"
                textColor="text.primary"
                borderColor="border.primary"
              />
            )}
          </TeContainerSlim>
          {address && (
            <TeContainer
              gap="xs"
              alignItems="center"
              backgroundColor="background.primary"
              borderColor="border.secondary"
              borderRadius="l"
              padding="xs"
              height="auto"
            >
              <TeIcon name="location_on" size="m" color="text.primary" />
              <TeText size={14} weight={400} color="text.primary">
                {address}
              </TeText>
            </TeContainer>
          )}
        </TeContainerSlim>
      </TeContainerSlim>

      <TeContainerSlim gap="s" alignItems="center">
        <TeButton
          variant="neutral"
          size="s"
          text={mode === 'connect' ? t('connect') : t('review')}
          teclick={() => onReview(suggestion)}
        />
        <TeContextMenuControl contextId={contextId}>
          <TeButtonMinimal variant="secondary" icon="more_vert" size="s" />
        </TeContextMenuControl>
        <TeContextMenu contextId={contextId} size="s">
          <TeContextMenuItem text={t('removeSuggestion')} teclick={() => onRemove(suggestion)} />
        </TeContextMenu>
      </TeContainerSlim>
    </TeContainer>
  );
}

export default CustomerSuggestionCard;
